import { useCallback, useState } from 'react';
import { Coupon } from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import {
  addBuyNowDiscount,
  removeBuyNowDiscount
} from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import { setBuyNowCouponData } from '@/modules/buy-now/infrastructure/slices';
import { useGetBuyNowCouponCartData } from '@/modules/perseo-billing/containers/billing/Summary/hooks/use-get-buy-now-coupon-data';
import { BuyNowEventInteractiveTags } from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import BuyNowCouponInfo from '@/Pages/BuyNowPage/PaymentAndShipping/partials/Coupon/BuyNowCouponInfo';
import RemoveCouponModal from '@/Pages/BuyNowPage/PaymentAndShipping/partials/Coupon/RemoveCouponModal';

export const BuyNowCouponComponent = () => {
  const [showRemoveCouponModal, setShowRemoveCouponModal] = useState(false);

  const { couponData, cart } = useAppSelector((state) => state.buyNow);
  const dispatch = useAppDispatch();
  const couponApplied = useGetBuyNowCouponCartData();

  const applyCoupon = (coupon: string) => {
    const couponCode = coupon || couponData?.value || '';
    const cartId = cart.data?.id;

    if (cartId) {
      dispatch(
        addBuyNowDiscount({
          discountCode: couponCode,
          cartId
        })
      );
    }
  };

  const removeCoupon = () => {
    const discountCode = couponApplied?.coupons?.[0]?.couponId ?? '';
    const cartId = cart.data?.id;

    try {
      dispatch(setBuyNowCouponData(null));
      if (cartId && discountCode) {
        dispatch(removeBuyNowDiscount({ discountCode, cartId }));
      }
    } catch (error) {
      console.error(
        `Error removing coupon ${discountCode} from cart ${cartId}:`,
        error
      );
    }
  };

  const couponProps = {
    coupon: couponData?.value || '',
    buttonDisabled: couponData?.buttonDisabled ?? true,
    status: couponData?.status,
    value: couponData?.value || '',
    helperText: couponData?.helperText || '',
    coupons: couponApplied?.coupons,
    couponName: couponApplied?.coupons?.[0]?.couponName || '',
    onAdd: applyCoupon,
    removeCoupon: removeCoupon
  };
  const handleCloseModal = useCallback(() => {
    setShowRemoveCouponModal(false);
  }, []);

  const handleAddCoupon = (couponCode: string) => {
    sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': BuyNowEventInteractiveTags.AddCouponButton
      }
    });
    couponProps.onAdd(couponCode);
  };

  const handleRemoveCoupon = useCallback(() => {
    sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': BuyNowEventInteractiveTags.RemoveCouponButton
      }
    });
    setShowRemoveCouponModal(true);
  }, []);

  return (
    <>
      {couponApplied?.coupons?.length ? (
        <BuyNowCouponInfo
          iconName="il-coupon"
          discountLabel="Descuento:"
          discountAmount={couponApplied.coupons[0]?.discountAmount || '$0'}
          couponLabel="Cupón:"
          couponName={couponApplied.coupons[0]?.couponName || ''}
          variant={
            couponApplied.coupons[0]?.variant === 'error' ? 'error' : 'positive'
          }
          onRemove={handleRemoveCoupon}
        />
      ) : (
        <Coupon
          key={couponData?.status === 'error' ? couponData.value : 'default'}
          {...couponProps}
          isFastCheckout
          onAdd={(couponCode: string) => handleAddCoupon(couponCode)}
          onRemove={() => handleRemoveCoupon()}
        />
      )}

      <RemoveCouponModal
        onClose={handleCloseModal}
        isOpen={showRemoveCouponModal}
        removeCoupon={couponProps?.removeCoupon}
      />
    </>
  );
};

BuyNowCouponComponent.displayName = 'BuyNowCouponComponent';
export default BuyNowCouponComponent;
